import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ENV } from 'shared/ENV';
import { useMapInstance, LngLatBounds } from 'shared/map-exports';
import { useMapEvent } from 'features/mapping/hooks/useMapEvent';
import { ParticleLayer } from 'weatherlayers-gl';
// @ts-ignore - ignore import error caused by us using an older import strategy or something
import { Client } from 'weatherlayers-gl/client';
import { Color } from '@deck.gl/core';
import { useMapLayersState } from 'state/useMapLayersState';
import { getMapBoundsInteger } from 'features/mapping/mapping.utils';
import {
  DeckLayerGroups,
  useDeckLayers,
} from 'features/mapping/components/DeckOverlay';

// Number of ms before refreshing wind data.
const WIND_DATA_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

const WEATHER_LAYERS_TOKEN = ENV.VITE_WEATHERLAYERS_API_KEY;

// Use HRRR when looking at CONUS, but GFS for the rest of the world, depending on viewport and zoom.
const DATASET_NAME_HRRR = 'hrrr_conus/wind_10m_above_ground';
const DATASET_NAME_GFS = 'gfs/wind_10m_above_ground';

// Bounds for CONUS (continental United States) in the format: [west, south, east, north].
// This has been adjusted to provide the best user experience.
const CONUS_BOUNDS: [number, number, number, number] = [
  -135.0, 21.0, -61.0, 55.0,
];

// Number of particles for a screen with width 1000px.
const NUM_PARTICLES = 400;

// Screen width for which the number of particles is calculated.
const SCREEN_CONSTANT = 1000;

// Maximum number of particles.
const MAX_PARTICLES = NUM_PARTICLES;

// How long the particles stay on screen (and how long they are as a result).
const PARTICLE_MAX_AGE = 40;

// Speed multiplier for particles (also affects how long they get).
const PARTICLE_SPEED_FACTOR = 15;

// Opacity of the particles for light or dark base layers.
const PARTICLE_OPACITY_LIGHT = 0.5;
const PARTICLE_OPACITY_DARK = 0.4;

// Particle color for light or dark base layers.
const PARTICLE_COLOR_LIGHT: Color = [100, 100, 100];
const PARTICLE_COLOR_DARK: Color = [255, 255, 255];

// Size / line width of the particles
const PARTICLE_LINE_WIDTH = 2.2;

// Types borrowed from: https://docs.weatherlayers.com/weatherlayers-cloud/types
type TextureData = {
  data: Uint8Array | Uint8ClampedArray | Float32Array;
  width: number;
  height: number;
};

type DatasetData = {
  datetime: string;
  referenceDatetime: string;
  horizon: string;
  image: TextureData;
  datetime2: string | null;
  referenceDatetime2: string | null;
  horizon2: string | null;
  image2: TextureData | null;
  imageWeight: number;
  imageType: unknown;
  imageUnscale: [number, number] | null;
  bounds: [number, number, number, number];
};

const fetchWindData = (datasetName: string): Promise<DatasetData> => {
  const client = new Client({
    accessToken: WEATHER_LAYERS_TOKEN,
  });
  return client.loadDatasetData(datasetName);
};

export const useMapIntegerViewport = (
  visible: boolean,
): LngLatBounds | undefined => {
  const map = useMapInstance();
  const [bounds, setBounds] = useState<LngLatBounds | undefined>();

  useMapEvent(
    'moveend',
    () => {
      setBounds(getMapBoundsInteger(map));
    },
    map,
    visible,
  );

  // Seed bounds when the layer becomes visible. Otherwise bounds stays stale
  // (falling back to GFS even over CONUS) until the next `moveend`, since the
  // event listener is only registered while visible. We can remove this if/when
  // we remove the `visible` prop.
  useEffect(() => {
    if (visible && map) {
      setBounds(getMapBoundsInteger(map));
    }
  }, [visible, map]);

  return bounds;
};

export const WindLayer = (props: { visible: boolean }): JSX.Element | null => {
  const { visible } = props;
  const { isDarkBaseLayer } = useMapLayersState();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const viewport = useMapIntegerViewport(visible);

  const sourceDatasetName = useMemo(() => {
    if (!viewport) {
      return DATASET_NAME_GFS;
    }
    // If the user's viewport is within CONUS bounds, use HRRR dataset,
    // otherwise use GFS dataset.
    const conusBounds = new LngLatBounds(CONUS_BOUNDS);
    return conusBounds.contains(viewport.getNorthEast()) &&
      conusBounds.contains(viewport.getSouthWest())
      ? DATASET_NAME_HRRR
      : DATASET_NAME_GFS;
  }, [viewport]);

  const numParticles = useMemo(
    () =>
      Math.floor(
        NUM_PARTICLES * ((windowWidth ?? SCREEN_CONSTANT) / SCREEN_CONSTANT),
      ),
    [windowWidth],
  );

  useEffect(() => {
    const handleResize = (): void => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { data } = useQuery({
    queryKey: ['wind-data', sourceDatasetName],
    queryFn: () => fetchWindData(sourceDatasetName),
    refetchInterval: WIND_DATA_REFRESH_INTERVAL_MS,
    enabled: visible,
  });

  const layers = useMemo(
    () =>
      data
        ? [
            new ParticleLayer({
              id: 'particle',
              image: data.image,
              imageUnscale: data.imageUnscale,
              bounds: data.bounds,
              numParticles:
                numParticles > MAX_PARTICLES ? MAX_PARTICLES : numParticles,
              maxAge: PARTICLE_MAX_AGE,
              speedFactor: PARTICLE_SPEED_FACTOR,
              color: isDarkBaseLayer
                ? PARTICLE_COLOR_DARK
                : PARTICLE_COLOR_LIGHT,
              width: PARTICLE_LINE_WIDTH,
              opacity: isDarkBaseLayer
                ? PARTICLE_OPACITY_DARK
                : PARTICLE_OPACITY_LIGHT,
              visible,
            }),
          ]
        : [],
    [data, numParticles, isDarkBaseLayer, visible],
  );

  useDeckLayers(DeckLayerGroups.Wind, layers);

  return null;
};
